import { ChangeEvent, useMemo } from "react";
import classNames from "classnames";
import Form from "react-bootstrap/Form";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { ClickableIcon } from "../clickable-icon/clickable-icon";
import { IdwFormLabelWithIconContainer } from "../idw-form-label-with-icon-container/idw-form-label-with-icon-container";
import styles from "./input-form-group.less";

type InputFormGroupProps = {
    controlId: string;
    labelWidth?: LabelWidthTypes;
    className?: string;
    automationId?: string;
    ariaLabel?: string;
    placeholder?: string;
    label: string;
    value: string;
    type?: string;
    accept?: string;
    min?: number;
    maxLength?: number;
    onChange: (event: ChangeEvent<HTMLInputElement>) => void;
    onBlur?: () => void;
    layout?: LayoutTypes;
    disabled: boolean;
    isRequired: boolean;
    readOnly?: boolean;
    autoComplete?: "on" | "off";
    beforeInputElement?: React.ReactNode;
    afterLabelTooltipText?: string;
    afterLabelIcon?: IconDefinition;
    afterLabelIconDisabled?: boolean;
    afterLabelIconOnClick?: () => void;
    afterInputElement?: React.ReactNode;
    afterInputIcon?: IconDefinition;
    afterInputIconDisabled?: boolean;
    afterInputIconOnClick?: () => void;
    afterInputIconContainerClassName?: string;
    rightAlignedLabelElement?: React.ReactNode;
};

export const InputFormGroup = (props: InputFormGroupProps) => {
    const {
        controlId,
        labelWidth,
        className,
        automationId,
        ariaLabel,
        placeholder,
        label,
        value,
        type = "text",
        accept,
        maxLength,
        min,
        onChange,
        onBlur,
        layout,
        disabled,
        isRequired,
        readOnly,
        autoComplete,
        beforeInputElement,
        afterLabelIcon,
        afterLabelIconDisabled,
        afterLabelIconOnClick,
        afterLabelTooltipText,
        afterInputElement,
        afterInputIcon,
        afterInputIconDisabled,
        afterInputIconOnClick,
        afterInputIconContainerClassName,
        rightAlignedLabelElement
    } = props;

    const formGroupClassName = useMemo(() => {
        return layout === LayoutTypes.COLUMN ? styles.inputFormGroupColumn : styles.inputFormGroupRow;
    }, [layout]);

    return (
        <Form.Group controlId={controlId} className={classNames(formGroupClassName, className)}>
            {label && (
                <IdwFormLabelWithIconContainer
                    controlId={controlId}
                    labelWidth={labelWidth}
                    layout={layout}
                    label={label}
                    isRequired={isRequired}
                    afterLabelTooltipText={afterLabelTooltipText}
                    afterLabelIcon={afterLabelIcon}
                    afterLabelIconDisabled={afterLabelIconDisabled}
                    afterLabelIconOnClick={afterLabelIconOnClick}
                    rightAlignedLabelElement={rightAlignedLabelElement}
                />
            )}
            <div className={styles.formGroupInputContainer}>
                {beforeInputElement ? beforeInputElement : null}
                <Form.Control
                    as="input"
                    required={isRequired}
                    automation-id={automationId}
                    aria-label={ariaLabel}
                    placeholder={placeholder}
                    onChange={onChange}
                    onBlur={onBlur}
                    type={type}
                    min={min}
                    maxLength={maxLength}
                    accept={accept}
                    autoComplete={autoComplete ? autoComplete : "off"}
                    value={value}
                    disabled={disabled}
                    readOnly={readOnly}
                />
                {afterInputIcon && afterInputIconOnClick && (
                    <div className={afterInputIconContainerClassName}>
                        <ClickableIcon
                            controlId={controlId}
                            disabled={afterInputIconDisabled}
                            icon={afterInputIcon}
                            onClick={afterInputIconOnClick}
                            className={styles.afterInputIcon}
                        />
                    </div>
                )}
                {afterInputElement ? afterInputElement : null}
            </div>
        </Form.Group>
    );
};